import React from 'react';
import LogoElitePrime from '@assets/images/Logotipo/Elite Prime_Logo_FundoPreto.png';
import { Styles } from './styles';
import { useNavigate } from 'react-router-dom';
import {
  useMediaQuery,
  useTheme,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import Navbar from '@components/Navbar';
import { AuthenticatedTabs } from '../../../../utils/constants';
import { useUserStore } from '@stores/User/User';
import { UserRole } from '@stores/User/types';

// const navLinks = [
//   { label: 'Imóveis', path: AuthenticatedTabs.IMOVEIS },
//   { label: 'Arquivos', path: AuthenticatedTabs.ARQUIVOS },
//   { label: 'Configurações', path: AuthenticatedTabs.CONFIGURACOES }
// ];

interface HeaderAdminProps {
  selectedTab: AuthenticatedTabs;
  setSelectedTab: (tab: AuthenticatedTabs) => void;
}

const HeaderAdmin = ({ selectedTab, setSelectedTab }: HeaderAdminProps) => {
  const { logOut, role, user, updateUserAvatar } = useUserStore();
  const isAdmin = role === UserRole.ADMIN;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  // Estados para o overlay de carregamento e mensagens
  const [loading, setLoading] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');

  const navLinks = [
    {
      label: 'Imóveis',
      disabled: false,
      path: AuthenticatedTabs.IMOVEIS,
      route: '/area-do-corretor/imoveis/listagem'
    }
  ];

  if (isAdmin) {
    navLinks.push(
      {
        label: 'Arquivos',
        disabled: false,
        path: AuthenticatedTabs.ARQUIVOS,
        route: '/area-do-corretor/arquivos/arquivados'
      },
      {
        label: 'Configurações',
        disabled: false,
        path: AuthenticatedTabs.CONFIGURACOES,
        route: '/area-do-corretor/configuracoes/codigo-de-imovel'
      }
    );
  } else {
    navLinks.push({
      label: 'Conta',
      disabled: false,
      path: AuthenticatedTabs.CONTA,
      route: '/area-do-corretor/conta'
    });
  }

  const handleTabClick = (tab: AuthenticatedTabs, route: string) => {
    setSelectedTab(tab);
    navigate(route);
    navigate(0);
  };

  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    const file = e.target.files?.[0];
    if (!file) {
      setLoading(false);
      return;
    }

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Por favor, selecione um arquivo de imagem válido.');
      setLoading(false);
      return;
    }

    // Validar tamanho do arquivo (máximo 5MB)
    const maxSizeInMB = 5;
    if (file.size > maxSizeInMB * 1024 * 1024) {
      setErrorMessage(`O arquivo deve ter no máximo ${maxSizeInMB}MB.`);
      setLoading(false);
      return;
    }

    try {
      const success = await updateUserAvatar(file);
      if (!success) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        setErrorMessage(
          'Erro ao atualizar o avatar. Tente novamente mais tarde ou com outra imagem.'
        );
      } else {
        setSuccessMessage('Avatar atualizado com sucesso!');
      }
    } catch (error) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setErrorMessage('Erro ao atualizar o avatar. Tente novamente.');
    } finally {
      setLoading(false);
    }

    // permite re-selecionar o mesmo arquivo depois
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleLogout = () => {
    setTimeout(logOut, 1000);
    navigate('/login');
  };

  return (
    <>
      <Styles.HeaderContainer>
        <Styles.HeaderArea>
          <Styles.LogoArea onClick={() => navigate('/')}>
            <Styles.LogoImage src={LogoElitePrime} alt="Logo" />
          </Styles.LogoArea>
          <Styles.TextArea>
            <Styles.TextTop>SISTEMA DE GERENCIAMENTO</Styles.TextTop>
          </Styles.TextArea>

          {/* Nova área de perfil (lado direito) */}
          <Styles.ProfileArea>
            <Styles.ProfileBlock>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
              />
              <Styles.AvatarCircle
                onClick={handleAvatarClick}
                aria-label="Trocar avatar">
                {user?.avatar ? (
                  <Styles.AvatarImg
                    src={user?.avatar}
                    alt="Avatar do usuário"
                  />
                ) : (
                  <Styles.AvatarPlaceholder>+</Styles.AvatarPlaceholder>
                )}
              </Styles.AvatarCircle>
              {user?.id ? (
                <Styles.ProfileLink
                  to={`/area-do-corretor/usuarios/${user?.id}`}>
                  {user?.name || 'Usuário'}
                </Styles.ProfileLink>
              ) : (
                <Styles.ProfileName>
                  {user?.name || 'Usuário'}
                </Styles.ProfileName>
              )}
            </Styles.ProfileBlock>
          </Styles.ProfileArea>
        </Styles.HeaderArea>
      </Styles.HeaderContainer>
      {!isMobile && (
        <Styles.GreenBar>
          <Styles.NavLinksContainer>
            <Styles.GreenBarSpacer />
            <Styles.NavTabsGroup>
              {navLinks.map((link) => (
                <Styles.NavLink
                  key={link.path}
                  to={link.route}
                  style={{
                    pointerEvents: link.disabled ? 'none' : 'auto',
                    opacity: link.disabled ? 0.5 : 1
                  }}
                  onClick={() => handleTabClick(link.path, link.route)}
                  isactive={selectedTab === link.path ? 'true' : 'false'}>
                  {link.label}
                  {link.disabled ? ' (Em breve)' : ''}
                </Styles.NavLink>
              ))}
            </Styles.NavTabsGroup>
            <Styles.GreenBarActions>
              <Styles.LogoutButton type="button" onClick={handleLogout}>
                <LogoutIcon
                  sx={{ fontSize: 15, mr: 0.5, verticalAlign: 'middle' }}
                />
                Sair
              </Styles.LogoutButton>
            </Styles.GreenBarActions>
          </Styles.NavLinksContainer>
        </Styles.GreenBar>
      )}
      {isMobile && <Navbar isAuthenticated={true} />}
      <Styles.MobileLogoutBar>
        <Styles.LogoutButton type="button" onClick={handleLogout}>
          <LogoutIcon sx={{ fontSize: 15, mr: 0.5, verticalAlign: 'middle' }} />
          Sair
        </Styles.LogoutButton>
      </Styles.MobileLogoutBar>

      {/* Overlay de Carregamento */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }}
        open={loading}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px'
          }}>
          <CircularProgress color="inherit" />
          <span style={{ fontSize: '16px', fontWeight: '500' }}>
            Atualizando avatar...
          </span>
        </div>
      </Backdrop>

      {/* Snackbar para Mensagens de Sucesso e Erro */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          onClose={() => setSuccessMessage('')}
          severity="success"
          sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={5000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          onClose={() => setErrorMessage('')}
          severity="error"
          sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default HeaderAdmin;
